import { useMemo, useEffect, useRef, useState, useCallback } from 'react'

import { i18n } from '../../../shared/i18n/i18n.js'
import { useI18n } from '../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { formatProductArea, formatTier } from '../../../shared/utils/productFormatters.js'
import { formatProductPrice, parsePricesVisibility } from '../../../shared/utils/index.js'
import { isProductAccessible } from '../../catalog/utils/unitTableSettings.js'
import { AVAILABILITY_STATUS } from '../../../shared/constants/availabilityStatus.js'

import { Button } from '../../../shared/ui/Button/index.js'
import { ChevronLeft } from '../../../shared/ui/icons/ChevronLeft.jsx'
import { ChevronRight } from '../../../shared/ui/icons/ChevronRight.jsx'
import { Close } from '../../../shared/ui/icons/Close.jsx'
import { DataGrid } from '../../../shared/ui/DataGrid/index.js'
import { Heart } from '../../../shared/ui/icons/Heart.jsx'
import { Pin } from '../../../shared/ui/icons/Pin.jsx'
import { PriceBlock } from '../../../shared/ui/PriceBlock/index.js'
import { ProductTile } from '../../../shared/ui/ProductTile/ProductTile.jsx'
import { StatusBadge } from '../../../shared/ui/StatusBadge/index.js'
import { Tooltip } from '../../../shared/ui/Tooltip/Tooltip.jsx'
import { UnitPlanThumb } from '../../../shared/ui/UnitPlanThumb/index.js'

import styles from './FavoritesListBlock.module.scss'

const HOLD_SCROLL_INTERVAL_MS = 150
const PIN_ROTATION_DELAY_MS = 100
const PIN_ROTATION_DURATION_MS = 250

const TILE_FIELD_MAP = {
  ref_tier: {
    labelKey: i18n.plural('common.floor', 1),
    getValue: (product) => formatTier(product),
  },
  rooms: {
    // labelKey: i18n.plural('common.room', 1),
    labelKey: 'common.rooms',
    getValue: (product) => product.rooms ?? null,
  },
  area: {
    labelKey: 'common.area_m2',
    getValue: (product) => formatProductArea(product),
  },
}

const TILE_COLUMN_ORDER = ['ref_tier', 'rooms', 'area']

function computeSensibleTileColumns(products) {
  return TILE_COLUMN_ORDER
    .filter((id) => TILE_FIELD_MAP[id])
    .filter((id) => {
      const { getValue } = TILE_FIELD_MAP[id]
      const unique = new Set(products.map((product) => getValue(product)).filter((value) => value != null))
      return unique.size > 0
    })
    .map((id) => ({ id, ...TILE_FIELD_MAP[id] }))
}

function stopTileActionEvent(event) {
  event.preventDefault()
  event.stopPropagation()
}

function getTileScrollStep(container) {
  const tile = container?.firstElementChild
  if (!tile) return 300
  const gap = parseFloat(getComputedStyle(container).gap) || 16
  return tile.offsetWidth + gap
}

function scrollByTile(container, direction) {
  if (!container) return
  const step = getTileScrollStep(container)
  container.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' })
}

/**
 * Horizontal favorites tiles container with optional pinned tiles area.
 * The component is presentational and fully driven by props.
 */
export function FavoritesListBlock({
  isMobile = false,
  products = [],
  pinnedProducts = [],
  isPinned = () => false,
  canPin = () => true,
  wireScrollListeners,
  panelSettings = null,
  projectDataUrl = null,
  logoSrc = null,
  use3dThumbs = false,
  onProductClick,
  onToggleFavorite,
  onTogglePin,
  reservedUnitsAccessible,
	reservedUnitsPlanVisible, 
	soldUnitsAccessible,      
	soldUnitsPlanVisible,
}) {

  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()

  // ── Derived state ─────────────────────────────────────────────────────────
  const [hasOverflow, setHasOverflow] = useState(false)
  const [showLeftChevron, setShowLeftChevron] = useState(false)
  const [showRightChevron, setShowRightChevron] = useState(false)
  const [pinAnimation, setPinAnimation] = useState(null)
  const productsToRender = useMemo(
    () => products.filter((product) => !pinnedProducts.some((p) => p.uuid === product.uuid)),
    [pinnedProducts, products],
  )
  const sensibleColumns = useMemo(
    () => computeSensibleTileColumns(products),
    [products],
  )
  const pricesVisibility = parsePricesVisibility(panelSettings)

  const scrollSegmentRef = useRef(null)
  const scrollIntervalRef = useRef(null)

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof wireScrollListeners !== 'function') return undefined
    return wireScrollListeners(scrollSegmentRef.current)
  }, [wireScrollListeners])

  useEffect(() => {
    const container = scrollSegmentRef.current
    if (!container) return undefined

    const syncChevrons = () => {
      const maxScrollLeft = container.scrollWidth - container.clientWidth
      const overflow = maxScrollLeft > 8
      setHasOverflow(overflow)
      setShowLeftChevron(container.scrollLeft > 8)
      setShowRightChevron(overflow && container.scrollLeft < maxScrollLeft - 8)
    }

    syncChevrons()
    container.addEventListener('scroll', syncChevrons, { passive: true })
    window.addEventListener('resize', syncChevrons)

    return () => {
      container.removeEventListener('scroll', syncChevrons)
      window.removeEventListener('resize', syncChevrons)
    }
  }, [productsToRender.length, pinnedProducts?.uuid])

  // ── Callbacks ─────────────────────────────────────────────────────────────
  const handleProductClick = (product) => {
    if (typeof onProductClick === 'function') {
      onProductClick(product)
    }
  }

  const handleToggleFavorite = (event, product) => {
    stopTileActionEvent(event)
    if (typeof onToggleFavorite === 'function') {
      onToggleFavorite(product)
    }
  }

  const handleTogglePin = (event, product) => {
    stopTileActionEvent(event)
    setPinAnimation({ uuid: product.uuid, nextPinned: !isPinned(product.uuid) })
    if (typeof onTogglePin === 'function') {
      onTogglePin(product)
    }
  }

  const handleChevronStep = useCallback((direction) => {
    scrollByTile(scrollSegmentRef.current, direction)
  }, [])

  const stopContinuousScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }, [])

  const startContinuousScroll = useCallback((direction) => {
    handleChevronStep(direction)
    scrollIntervalRef.current = setInterval(() => {
      scrollByTile(scrollSegmentRef.current, direction)
    }, HOLD_SCROLL_INTERVAL_MS)
  }, [handleChevronStep])

  useEffect(() => () => stopContinuousScroll(), [stopContinuousScroll])

  useEffect(() => {
    if (!pinAnimation) return undefined

    const timeoutId = window.setTimeout(() => {
      setPinAnimation(null)
    }, PIN_ROTATION_DELAY_MS + PIN_ROTATION_DURATION_MS + 50)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [pinAnimation])

  // ── Render ────────────────────────────────────────────────────────────────
  const chevronLeftClass = [
    styles.chevronButton,
    styles.chevronButtonLeft,
    !hasOverflow || !showLeftChevron ? styles.chevronHidden : '',
  ].filter(Boolean).join(' ')

  const chevronRightClass = [
    styles.chevronButton,
    styles.chevronButtonRight,
    !hasOverflow || !showRightChevron ? styles.chevronHidden : '',
  ].filter(Boolean).join(' ')

  const scrollTilesSegmentClass = [
    styles.scrollTilesSegment,
    pinnedProducts.length > 0 ? styles.scrollTilesSegmentGapped : '',
  ].filter(Boolean).join(' ')

  return (
    <>
      <div className={styles.listBlock}>
        { !isMobile && (
          <Button
            type="button"
            kind="icon"
            size="large"
            className={chevronLeftClass}
            aria-label={t('common.previous', 'Previous')}
            disabled={!showLeftChevron}
            onClick={() => handleChevronStep('left')}
            onMouseDown={() => startContinuousScroll('left')}
            onMouseUp={stopContinuousScroll}
            onMouseLeave={stopContinuousScroll}
            onTouchStart={() => startContinuousScroll('left')}
            onTouchEnd={stopContinuousScroll}
          >
            <ChevronLeft />
          </Button>
        )}
        <div className={styles.tilesFrame}>
          {pinnedProducts.length > 0 && (
            <div
              className={styles.pinnedTilesSegment}
              style={{ '--pinned-tiles-count': pinnedProducts.length }}
            >
              {pinnedProducts.map((product) => (
                <FavoriteTileCard
                  key={product.uuid}
                  product={product}
                  isPinned={isPinned(product.uuid)}
                  isPinDisabled={false}
                  animatePinRotation={
                    pinAnimation?.uuid === product.uuid
                    && pinAnimation.nextPinned === isPinned(product.uuid)
                  }
                  sensibleColumns={sensibleColumns}
                  pricesVisibility={pricesVisibility}
                  projectDataUrl={projectDataUrl}
                  logoSrc={logoSrc}
                  use3dThumbs={use3dThumbs}
                  pinnedLayer
                  onProductClick={handleProductClick}
                  onToggleFavorite={handleToggleFavorite}
                  onTogglePin={handleTogglePin}
                  reservedUnitsAccessible={reservedUnitsAccessible}
                  reservedUnitsPlanVisible={reservedUnitsPlanVisible}
                  soldUnitsAccessible={soldUnitsAccessible}
                  soldUnitsPlanVisible={soldUnitsPlanVisible}
                />
              ))}
            </div>
          )}

          <div ref={scrollSegmentRef} className={scrollTilesSegmentClass}>
            {productsToRender.map((product) => (
              <FavoriteTileCard
                key={product.uuid}
                product={product}
                isPinned={isPinned(product.uuid)}
                isPinDisabled={!canPin(product.uuid) && !isPinned(product.uuid)}
                animatePinRotation={
                  pinAnimation?.uuid === product.uuid
                  && pinAnimation.nextPinned === isPinned(product.uuid)
                }
                sensibleColumns={sensibleColumns}
                pricesVisibility={pricesVisibility}
                projectDataUrl={projectDataUrl}
                logoSrc={logoSrc}
                use3dThumbs={use3dThumbs}
                onProductClick={handleProductClick}
                onToggleFavorite={handleToggleFavorite}
                onTogglePin={handleTogglePin}
                reservedUnitsAccessible={reservedUnitsAccessible}
                reservedUnitsPlanVisible={reservedUnitsPlanVisible}
                soldUnitsAccessible={soldUnitsAccessible}
                soldUnitsPlanVisible={soldUnitsPlanVisible}
              />
            ))}
          </div>
        </div>

        { !isMobile && (
          <Button
            type="button"
            kind="icon"
            size="large"
            className={chevronRightClass}
            aria-label={t('common.next', 'Next')}
            disabled={!showRightChevron}
            onClick={() => handleChevronStep('right')}
            onMouseDown={() => startContinuousScroll('right')}
            onMouseUp={stopContinuousScroll}
            onMouseLeave={stopContinuousScroll}
            onTouchStart={() => startContinuousScroll('right')}
            onTouchEnd={stopContinuousScroll}
          >
            <ChevronRight />
          </Button>
        )}
      </div>
    </>
  )
}

/**
 * Single favorites tile card used by the list container.
 */
function FavoriteTileCard({
  product,
  isPinned,
  isPinDisabled,
  animatePinRotation = false,
  pinnedLayer = false,
  sensibleColumns = [],
  pricesVisibility = {},
  projectDataUrl = null,
  logoSrc = null,
  use3dThumbs = false,
  onProductClick,
  onToggleFavorite,
  onTogglePin,
  reservedUnitsAccessible,
  reservedUnitsPlanVisible, 
  soldUnitsAccessible,      
  soldUnitsPlanVisible,
}) {
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings()
  const [pinVisualPinned, setPinVisualPinned] = useState(() => (
    animatePinRotation ? !isPinned : isPinned
  ))

  const planVisible = 
      product?.status === AVAILABILITY_STATUS.AVAILABLE ||
      (product?.status === AVAILABILITY_STATUS.RESERVED && reservedUnitsPlanVisible ) ||
      (product?.status === AVAILABILITY_STATUS.SOLD && soldUnitsPlanVisible )
  
  const isAccessible = isProductAccessible(product, {reservedUnitsAccessible, soldUnitsAccessible});
      
  useEffect(() => {
    if (!animatePinRotation) {
      setPinVisualPinned(isPinned)
      return undefined
    }

    setPinVisualPinned(!isPinned)
    const timeoutId = window.setTimeout(() => {
      setPinVisualPinned(isPinned)
    }, PIN_ROTATION_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [animatePinRotation, isPinned])

  const arePricesVisible = pricesVisibility.pricesVisible !== false
  const pricesSoldVisible = pricesVisibility.pricesSoldVisible !== false
  const pricesReservedVisible = pricesVisibility.pricesReservedVisible !== false

  const { displayPrice, isHidden, isOnOffer, strikethroughPrice } = formatProductPrice(product, {
    pricesVisible: arePricesVisible,
    pricesSoldVisible,
    pricesReservedVisible,
    hiddenText: '—',
    hiddenSoldText: '-',
    locale: (i18n.locale ?? 'pl_PL').replace('_', '-'),
  })

  const dataItems = sensibleColumns.map((column) => ({
    label: t(column.labelKey),
    value: column.getValue(product) ?? '—',
  }))

  const tileHolderClass = [
    styles.tileHolder,
    pinnedLayer ? styles.tilePinned : styles.tileScrollable,
  ].filter(Boolean).join(' ')

  const pinButtonClass = [
    styles.pinButton,
  ].filter(Boolean).join(' ')

  const pinIconClass = [
    styles.pinIcon,
    pinVisualPinned ? styles.pinIconPinned : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={tileHolderClass}>
      <ProductTile
        variant="grid"
        className={styles.tileCard}
        role="button"
        tabIndex={-1}
        aria-label={product.id_cad ?? product.uuid}
      >
        <ProductTile.Header>
          <div className={styles.tileHeader} >
            <Button
              kind="icon"
              size="xsmall"
              selected
              className={styles.heartRemoveButton}
              aria-label={t('favorites.remove')}
              onClick={(event) => onToggleFavorite(event, product)}
            >
              <span className={styles.heartIconBox}>
                <span className={styles.heartIconDefault}><Heart filled /></span>
                <span className={styles.heartIconRemove} aria-hidden="true"><Close/></span>
              </span>
            </Button>
          </div>
        </ProductTile.Header>

        <ProductTile.Content>
          {projectDataUrl && (
            <UnitPlanThumb
              product={product}
              projectDataUrl={projectDataUrl}
              logoSrc={logoSrc}
              planVisible={planVisible}
              use3d={use3dThumbs}
              className={styles.planThumb}
            />
          )}
          <div className={styles.statusRow}>
            <StatusBadge status={product.status} size="small" />
          </div>

          <DataGrid
            columns={3}
            size="tiny"
            items={dataItems}
            className={styles.dataGridRoot}
            gridClassName={styles.dataGrid}
            footer={arePricesVisible ? (
              <PriceBlock
                displayPrice={displayPrice}
                strikethroughPrice={strikethroughPrice}
                isOnOffer={isOnOffer}
                isHidden={isHidden}
                label={t('common.price')}
                highlight
              />
            ) : null}
          />
        </ProductTile.Content>

        <ProductTile.Action>
          <Button
            type="button"
            kind="wide"
            size="large"
            cta={isAccessible}
            disabled={!isAccessible}
            className={styles.tileActionButton}
            onClick={(event) => {
              event.stopPropagation()
              onProductClick(product)
            }}
            onTouchEnd={(event) => event.stopPropagation()}
          >
            {`${getProductsCategoryString('open', product.category, {id: product.id_original})}`}
          </Button>
        </ProductTile.Action>
      </ProductTile>

      <div className={styles.pinButtonBox}>
        <Tooltip 
          title={
            isPinDisabled 
              ? t('favorites.pin_disabled') 
              : (isPinned 
                 ? t('favorites.unpin_from_compare') 
                 : t('favorites.pin_and_compare') 
                )
          }
          placement="bottom"
        >
          <Button
            kind="icon"
            size="small"
            selected={isPinned}
            disabled={isPinDisabled || !isAccessible}
            className={pinButtonClass}
            aria-label={isPinned ? t('favorites.unpin_from_compare') : t('favorites.pin_and_compare')}
            onClick={(event) => onTogglePin(event, product)}
          >
            <Pin pinned={isPinned} className={pinIconClass} />
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
